import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { isAdmin, logSecurityEvent } from "@/lib/dashboardAuth";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Admin Login | Homestead Construction" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // OAuth redirect handler: if a session already exists, verify admin and bounce in.
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      setVerifying(true);
      const { data, error } = await supabase.functions.invoke("verify-oauth-admin", { body: {} });
      if (error || !data?.ok) {
        await supabase.auth.signOut();
        setVerifying(false);
        await logSecurityEvent("oauth_unauthorized", false, session.user.email ?? null);
        toast.error("This account isn't authorized to access the dashboard.");
        return;
      }
      await logSecurityEvent("oauth_login_success", true, session.user.email ?? null);
      navigate({ to: "/dashboard", replace: true });
    })();
  }, [navigate]);

  // Password sign-in: validate, then trigger a 6-digit code via Supabase's
  // built-in OTP and send the user to /auth/otp.
  const onSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.user) {
      setLoading(false);
      await logSecurityEvent("login_failed", false, email);
      toast.error("Invalid credentials.");
      return;
    }
    const admin = await isAdmin(data.user.id);
    if (!admin) {
      await supabase.auth.signOut();
      setLoading(false);
      await logSecurityEvent("login_unauthorized", false, email);
      toast.error("This account is not authorized.");
      return;
    }
    // Password is good — drop the session and require the email code.
    await supabase.auth.signOut();
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    setLoading(false);
    if (otpErr) {
      toast.error("Couldn't send the verification code.");
      return;
    }
    await logSecurityEvent("login_otp_sent", true, email);
    navigate({ to: "/auth/otp", search: { email } });
  };

  const oauth = async (provider: "google" | "apple") => {
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: `${window.location.origin}/auth`,
    });
    if (result.error) {
      toast.error("Couldn't start sign-in. Please try again.");
      return;
    }
    if (result.redirected) return;
    window.location.href = "/auth";
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-alt">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Verifying access…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-alt flex items-center justify-center p-4">
      <form
        onSubmit={onSignIn}
        className="w-full max-w-md bg-card border border-border rounded-2xl p-8 space-y-5 shadow-xl"
      >
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Homestead Construction</h1>
          <p className="text-muted-foreground text-sm">Admin Dashboard</p>
        </div>
        <div>
          <label className="text-sm text-foreground mb-1 block">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-background border border-border focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="text-sm text-foreground mb-1 block">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-background border border-border focus:border-brand focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-brand hover:bg-brand-hover text-white font-display font-bold transition-colors disabled:opacity-50"
        >
          {loading ? "Sending code…" : "Continue"}
        </button>
        <p className="text-xs text-muted-foreground text-center">
          A 6-digit code will be sent to your email.
        </p>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-card px-2 text-muted-foreground">or</span>
          </div>
        </div>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => oauth("google")}
            className="w-full py-3 rounded-lg bg-background border border-border hover:border-brand font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <button
            type="button"
            onClick={() => oauth("apple")}
            className="w-full py-3 rounded-lg bg-background border border-border hover:border-brand font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continue with Apple
          </button>
        </div>

        <Link
          to="/"
          className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-2"
        >
          <ArrowLeft size={12} /> Back to homepage
        </Link>
      </form>
    </div>
  );
}
